import React, { useState } from 'react'
import { Box, Chip, IconButton, Popover, Tooltip, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { useToasts } from 'react-toast-notifications'
import Loader from 'src/designSystem/Loader'
import { trackAddParticipantTag, trackRemoveParticipantTag } from 'src/mixpanel/api'
import {
  GetAdvancedPatientTagsDocument,
  useAddAdvancedTagToParticipantMutation,
  useGetAdvancedAllTagsLazyQuery,
  useGetAdvancedPatientTagsQuery,
  useRemoveAdvancedTagFromParticipantMutation,
} from '../api/advancedPatientTags.generated'
import { describeFailure } from '../api/failureReason'
import FailureMessage from './FailureMessage'
import { advancedColors, onDark } from '../theme'
import EmptyNote from './EmptyNote'
import Section from './Section'

const TagsSection = ({ uid, grouped = false }: { uid: string; grouped?: boolean }) => {
  const { data, loading } = useGetAdvancedPatientTagsQuery({ variables: { uid } })
  const [getAllTags, { data: allTagsData, loading: allTagsLoading }] = useGetAdvancedAllTagsLazyQuery()
  const [addTag] = useAddAdvancedTagToParticipantMutation()
  const [removeTag] = useRemoveAdvancedTagFromParticipantMutation()
  const { addToast } = useToasts()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  const patientTags = data?.patient?.tags ?? []
  const allTags = allTagsData?.patientTags ?? []
  const refetchQueries = [{ query: GetAdvancedPatientTagsDocument, variables: { uid } }]

  const toggle = async (tagID: string, applied: boolean) => {
    try {
      if (applied) {
        trackRemoveParticipantTag()
        await removeTag({ variables: { tagID, uid }, refetchQueries })
      } else {
        trackAddParticipantTag()
        await addTag({ variables: { tagID, uid }, refetchQueries })
      }
    } catch (error) {
      addToast(<FailureMessage failure={describeFailure('Failed to update the tags.', error)} />, {
        appearance: 'error',
      })
    }
  }

  let body: React.ReactNode = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {patientTags.map((tag) => (
        <Chip
          key={tag.id}
          size="small"
          label={tag.name}
          sx={{ backgroundColor: onDark.accentGreen, color: advancedColors.deep }}
        />
      ))}
    </Box>
  )
  if (loading && !data) {
    body = <Loader size="small" />
  } else if (patientTags.length === 0) {
    body = <EmptyNote>No tags</EmptyNote>
  }

  return (
    <Section
      grouped={grouped}
      title="Tags"
      action={
        <Tooltip title="Edit tags">
          <IconButton
            size="small"
            aria-label="Edit tags"
            onClick={(event) => {
              setAnchor(event.currentTarget)
              getAllTags().catch((error) =>
                addToast(<FailureMessage failure={describeFailure('Failed to load the tags.', error)} />, {
                  appearance: 'error',
                })
              )
            }}
            sx={{ color: onDark.secondary }}
          >
            <AddRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      {body}
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 2, maxWidth: 320 }}>
          {allTagsLoading && <Loader size="small" />}
          {!allTagsLoading && allTags.length === 0 && (
            <Typography variant="subtitle1" sx={{ color: advancedColors.inkMuted }}>
              No tags exist yet.
            </Typography>
          )}
          {!allTagsLoading &&
            allTags.map((tag) => {
              const applied = patientTags.some((patientTag) => patientTag.id === tag.id)

              return (
                <Chip
                  key={tag.id}
                  clickable
                  size="small"
                  label={tag.name}
                  color={applied ? 'primary' : undefined}
                  variant={applied ? 'filled' : 'outlined'}
                  onClick={() => toggle(tag.id, applied)}
                />
              )
            })}
        </Box>
      </Popover>
    </Section>
  )
}

export default TagsSection
